<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import TokenGauge from './components/TokenGauge.vue';

// Definimos las opciones del componente
defineOptions({
  name: 'OpenAiUsageIndex',
});

// Mapeamos el Getter global nativo de Chatwoot para la cuenta activa
const currentAccountId = useMapGetter('getCurrentAccountId');

// Estado reactivo de la vista
const loading = ref(true);
const usagePercentage = ref(0);
const currentUsageUSD = ref(0);
const limitUSD = ref(20);

// Propiedad computada para calcular el rango de fechas en español
const formattedDateRange = computed(() => {
  const today = new Date();
  const day = today.getDate();

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];
  const currentMonth = months[today.getMonth()];

  const startDateStr = `01-${currentMonth}`;
  const todayStr = `${day < 10 ? '0' + day : day}-${currentMonth}`;

  if (day === 1) {
    return startDateStr;
  }

  return `${startDateStr} al ${todayStr}`;
});

// Función para obtener los consumos desde nuestro backend seguro
async function fetchOpenAiUsage() {
  try {
    loading.value = true;
    
    // Extraemos el ID numérico real desde el getter global
    const accountId = currentAccountId.value;
    
    if (!accountId) {
      console.error("No se pudo mapear un ID de cuenta válido desde Chatwoot.");
      return;
    }

    // Petición Axios multi-tenant
    const response = await window.axios.get(`/api/v1/accounts/${accountId}/openai_usage`);
    
    // Asignamos los valores devueltos por el controlador de Rails
    usagePercentage.value = response.data.percentage;
    currentUsageUSD.value = response.data.current_usage_usd;
    limitUSD.value = response.data.limit_usd;
  } catch (error) {
    console.error("Error obteniendo consumos de OpenAI:", error);
  } finally {
    loading.value = false;
  }
}

// Ejecutar la petición al montar el componente en la UI
onMounted(() => {
  fetchOpenAiUsage();
});
</script>

<template>
  <div class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-surface-1">
    <section class="flex w-full h-full gap-4 overflow-hidden justify-evenly bg-n-surface-1">
      <div class="flex flex-col w-full h-full transition-all duration-300">
        <header class="sticky top-0 z-20 px-6">
          <div class="flex items-start sm:items-center justify-between w-full py-6 gap-2 mx-auto max-w-5xl">
            <span class="text-xl font-medium truncate text-n-slate-12">
              Consumo actual de los tokens del agente
            </span>
          </div>
        </header>
        <main class="flex-1 overflow-y-auto px-6">
          <div class="w-full mx-auto max-w-5xl">
            <p class="text-n-slate-11 mb-0 line-clamp-5 sm:line-clamp-none text-body-main">
              En esta sección, podrá monitorear el consumo de tokens del agente de IA durante el mes actual correspondiente al período del
              <span class="font-semibold text-slate-700 dark:text-slate-300">{{ formattedDateRange }}</span>. Esta información es crucial para gestionar eficientemente los recursos y optimizar el rendimiento del agente.
            </p>
            <br />
            <p class="text-n-slate-11 mb-0 line-clamp-5 sm:line-clamp-none text-body-main">
              Consumo: <span class="font-semibold text-slate-700 dark:text-slate-300">{{ currentUsageUSD }}</span> USD
            </p>
            <p class="text-n-slate-11 mb-0 line-clamp-5 sm:line-clamp-none text-body-main">
              Límite: <span class="font-semibold text-slate-700 dark:text-slate-300">{{ limitUSD }}</span> USD
            </p>
          </div>
          <slot name="default" />
          
          <div class="flex justify-center py-4">
            <!-- Loader nativo temporal mentre se procesa la API -->
            <div v-if="loading" class="flex flex-col items-center justify-center h-48">
              <span class="text-sm text-n-slate-11 animate-pulse">Obteniendo consumo en tiempo real...</span>
            </div>
            <!-- Medidor dinámico con los datos reales de OpenAI -->
            <TokenGauge v-else :percentage="usagePercentage" />
          </div>
        </main>
      </div>
    </section>
  </div>
</template>